/*!
 * ExamSpark ExamPlayer Associate Interaction v.0.1.0
 * Copyright (c) ExamSpark, LLC.
 */

import { produce } from "immer";
import React, { useState, useEffect, useContext } from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";

import { Stem, StemAssociate } from "@examspark/types";
import { Drop, Pair } from "@examspark/types/dist/item-stem";

import { hideTokenAfterUse } from "../common/drag/dndUtils";
import QuestionContext from "../QuestionContext";

import MatchPair from "./common/MatchPair";
import Prompt from "./shared/Prompt";
import Token from "./shared/Token";

interface AssociateProps {
  data: StemAssociate;
}
const Associate = (props: AssociateProps) => {
  const [show, setShow] = useState(false);
  const [player, dispatchPlayer] = useContext(QuestionContext);

  /**
   * Hack: DndProvider doens't like it when you unmount one provider and add a
   * new one in rapid fashion. Do this provided a little window to let the
   * providers move in/out without colliding.
   */
  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 25);
  }, []);

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      const keys: Stem.Pair[] = [];
      props.data.scoring?.pairs?.forEach((p) => {
        keys.push({
          src: p.src,
          dst: p.dst,
        });
      });

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            pairs: keys,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);

  if (!show) {
    return null;
  }

  const pairs: Pair[] = player.response?.[props.data.id]?.pairs || [];
  const dropsFromPairs = pairs
    .flatMap((p) => [
      {
        draggableId: p.src,
      },
      {
        draggableId: p.dst,
      },
    ])
    .filter((d): d is Pick<Drop, "draggableId"> => !!d.draggableId);

  const filteredTokens = hideTokenAfterUse(
    props.data.content.options.map((o) => ({
      type: "text",
      item: o,
    })),
    dropsFromPairs,
  );

  // This is a silly workaround. Students were able to duplicate tokens
  // by dragging them between gaps. By setting the token dragType to
  // something other than just `token`, it disable the ability to
  // drag between gaps altogether so I don't have to fix that.
  // The logic over in `GapMatch` tracks an `instanceId` that we dont, which
  // is why I cannot just steal their logic. Match uses a similar `token:pool1`
  // type which is where I stole the workaround idea from. When we fix this,
  // we will also want to update `Match` to support dragging between gaps
  // p.s to future me: `MatchCell` hard-coding a type of `token` is
  // what makes it special
  const useDragType = "token:pool1";
  return (
    <div className="item-player-element item-player-stem">
      <Prompt data={props.data.content.prompt} />
      {/* @ts-expect-error children */}
      <DndProvider
        backend={TouchBackend}
        key={props.data.id}
        options={{
          enableMouseEvents: true,
          enableHoverOutsideTarget: true,
        }}
        context={window}
      >
        {props.data.scoring?.pairs && (
          <div className="item-player-matches">
            {props.data.scoring.pairs.map((p, idx) => {
              const rsp = pairs.at(idx);
              return (
                <MatchPair
                  bidirectional
                  key={idx}
                  acceptLeft={[useDragType]}
                  acceptRight={[useDragType]}
                  response={rsp}
                  pairs={props.data.scoring?.pairs}
                  // index={idx}
                  dataSrc={props.data.content.options}
                  dataDst={props.data.content.options}
                  onDrop={(from, item) => {
                    const rsp = produce(pairs, (draft) => {
                      if (!draft[idx]) {
                        draft[idx] = {};
                      }

                      draft[idx][from] = item.id;
                    });

                    dispatchPlayer({
                      type: "RESPONSE",
                      data: {
                        id: props.data.id,
                        response: {
                          pairs: rsp,
                        },
                      },
                    });
                  }}
                  onLeave={(side) => {
                    const next = produce(pairs, (draft) => {
                      draft[idx][side] = undefined;
                    });

                    dispatchPlayer({
                      type: "RESPONSE",
                      data: {
                        id: props.data.id,
                        response: {
                          pairs: next,
                        },
                      },
                    });
                  }}
                />
              );
            })}
          </div>
        )}
        {props.data.content.options && (
          <div className="item-player-tokens">
            {filteredTokens.map((opt) => {
              return (
                <Token
                  key={opt.item.id}
                  data={opt.item}
                  useDragType={useDragType}
                />
              );
            })}
          </div>
        )}
      </DndProvider>
    </div>
  );
};
export default Associate;
